import React, { useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot } from "lucide-react";
import ChatEmptyState from "./ChatEmptyState";
import ChatComposer from "./ChatComposer";
import AssistantMarkdown from "./AssistantMarkdown";
import { ResearchWorkspace } from "./research";
import { type AssistantMode } from "./ModeSelector";
import { getStreamingStatusText } from "./agentConfig";
import { useAssistantChat } from "@/hooks/assistant/useAssistantChat";
import type { ChatMessage } from "@/services/assistant/AssistantService";
import type { MultiAgentStreamState } from "@/types/assistant";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface AssistantChatProps {
  conversationId?: string | undefined;
  authToken?: string | undefined;
  onConversationId?: ((conversationId: string, title?: string) => void) | undefined;
  onStreamingChange?: ((conversationId: string, streaming: boolean) => void) | undefined;
  onConversationsShouldRefresh?: (() => void) | undefined;
  prefill?: string | undefined;
  onPrefillUsed?: (() => void) | undefined;
  resetNonce?: number | undefined;
  /** Initial mode (uncontrolled) */
  initialMode?: AssistantMode;
  /** Controlled mode - when provided, parent controls the mode */
  mode?: AssistantMode;
  /** Callback when mode changes (for controlled mode) */
  onModeChange?: (mode: AssistantMode) => void;
  /** Serialized context for thesis-aware AI */
  context?: Record<string, unknown> | undefined;
  /** Whether running in side panel mode (compact layout) */
  panelMode?: boolean;
  /** Whether deep research is enabled (for Ask mode toggle) */
  isDeepResearch?: boolean;
  /** Callback to toggle deep research */
  onDepthToggle?: (isDeep: boolean) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Message Row Component
// ─────────────────────────────────────────────────────────────────────────────

interface MessageRowProps {
  message: ChatMessage;
  previousRole: ChatMessage["role"] | null;
  multiAgentState: MultiAgentStreamState | undefined;
  streaming: boolean;
  isLastAssistant: boolean;
  compact?: boolean;
}

// getStreamingStatusText is now imported from agentConfig.ts

const MessageRow: React.FC<MessageRowProps> = ({
  message,
  previousRole,
  multiAgentState,
  streaming,
  isLastAssistant,
  compact = false,
}) => {
  const isUser = message.role === "user";
  const showAvatar = previousRole !== message.role;
  
  // Show research workspace for deep research mode responses
  const showResearchWorkspace = !isUser && 
    multiAgentState && 
    multiAgentState.plannedAgents.length > 0 &&
    (streaming || isLastAssistant);

  // Get charts from multi-agent state
  const charts = useMemo(() => {
    return multiAgentState?.charts ?? new Map();
  }, [multiAgentState]);
  
  // Get dynamic status text for streaming
  const streamingStatus = useMemo(() => {
    return getStreamingStatusText(multiAgentState);
  }, [multiAgentState]);

  const toHardBreaks = (md: string) => md.replace(/([^\n])\n(?!\n)/g, "$1  \n");

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      {/* Assistant Avatar */}
      {!isUser && (
        <div className={`mr-3 mt-1 flex-shrink-0 ${showAvatar ? "opacity-100" : "opacity-0"}`}>
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex items-center justify-center border border-violet-500/30 shadow-lg shadow-violet-500/10">
            <Bot className="h-4 w-4 text-violet-400" />
          </div>
        </div>
      )}

      {/* Message Content */}
      <div className={`${isUser ? "max-w-[75%]" : "max-w-[90%]"} flex flex-col gap-3`}>
        {/* Research Workspace (for deep research mode) */}
        {showResearchWorkspace && (
          <ResearchWorkspace
            multiAgentState={multiAgentState}
            streaming={streaming}
            compact={compact}
          />
        )}

        {/* Message Bubble - hide when assistant message is empty during streaming */}
        {(isUser || message.content.length > 0) && (
          <div
            className={`
              rounded-2xl shadow-lg
              ${isUser 
                ? "bg-gradient-to-br from-violet-600/90 to-fuchsia-600/80 text-white px-4 py-3" 
                : "bg-gray-800/50 backdrop-blur-sm text-gray-100 px-5 py-4"
              }
            `}
            style={{
              boxShadow: isUser 
                ? "0 4px 20px rgba(139, 92, 246, 0.2)" 
                : "0 4px 20px rgba(0, 0, 0, 0.2)",
            }}
          >
            {isUser ? (
              <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                {message.content}
              </div>
            ) : (
              <AssistantMarkdown 
                content={toHardBreaks(message.content)} 
                charts={charts}
                enableRichContent={true}
              />
            )}
          </div>
        )}

        {/* Streaming status indicator */}
        {!isUser && streaming && isLastAssistant && message.content.length === 0 && (
          <div className="flex items-center gap-2.5 text-gray-400 h-9 mt-1">
            <motion.div
              className="h-4 w-1 bg-violet-400 rounded-full"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 0.8, repeat: Infinity }}
            />
            <span className="text-sm font-medium">{streamingStatus}</span>
          </div>
        )}
      </div>

      {/* User Avatar */}
      {isUser && (
        <div className={`ml-3 mt-1 flex-shrink-0 ${showAvatar ? "opacity-100" : "opacity-0"}`}>
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-500/30 to-fuchsia-500/30 flex items-center justify-center border border-violet-500/40">
            <span className="text-xs font-semibold text-violet-300">You</span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Chat Timeline
// ─────────────────────────────────────────────────────────────────────────────

interface ChatTimelineProps {
  messages: ChatMessage[];
  previousRoleByIndex: Array<ChatMessage["role"] | null>;
  multiAgentStateByMessage: Record<string, MultiAgentStreamState>;
  streaming: boolean;
  compact?: boolean;
}

const ChatTimeline: React.FC<ChatTimelineProps> = ({
  messages,
  previousRoleByIndex,
  multiAgentStateByMessage,
  streaming,
  compact = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, streaming]);

  // Find last assistant message index
  const lastAssistantIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === "assistant") return i;
    }
    return -1;
  }, [messages]);

  return (
    <div ref={scrollRef} className="space-y-4">
      {messages.map((msg, idx) => (
        <MessageRow
          key={msg.id}
          message={msg}
          previousRole={previousRoleByIndex[idx] ?? null}
          multiAgentState={multiAgentStateByMessage[msg.id]}
          streaming={streaming}
          isLastAssistant={idx === lastAssistantIndex}
          compact={compact}
        />
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const AssistantChat: React.FC<AssistantChatProps> = ({
  conversationId,
  authToken,
  onConversationId,
  onStreamingChange,
  onConversationsShouldRefresh,
  prefill,
  onPrefillUsed,
  resetNonce,
  initialMode = "deep",
  mode: controlledMode,
  onModeChange,
  context,
  panelMode = false,
  isDeepResearch,
  onDepthToggle,
}) => {
  // Support both controlled and uncontrolled mode
  const [internalMode, setInternalMode] = React.useState<AssistantMode>(initialMode);
  const isControlled = controlledMode !== undefined;
  const mode = isControlled ? controlledMode : internalMode;
  const setMode = React.useCallback((newMode: AssistantMode) => {
    if (isControlled) {
      onModeChange?.(newMode);
    } else {
      setInternalMode(newMode);
    }
  }, [isControlled, onModeChange]);

  const [state, actions] = useAssistantChat({
    conversationId,
    authToken,
    onConversationId,
    onStreamingChange,
    onConversationsShouldRefresh,
    resetNonce,
    mode,
    context,
  });

  const {
    messages,
    input,
    isLoading,
    isHydrating,
    showEmpty,
    multiAgentStateByMessage,
    previousRoleByIndex,
  } = state;

  const { setInput, sendMessage, stopStreaming } = actions;

  // ─── Prefill Effect ───
  useEffect(() => {
    if (prefill && !isLoading && input.trim().length === 0) {
      setInput(prefill);
      onPrefillUsed?.();
    }
  }, [prefill, isLoading, input, setInput, onPrefillUsed]);

  // ─── Suggestion Handler ───
  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    void sendMessage();
  };

  // ─── Loading State ───
  if (isHydrating) {
    return (
      <div className="flex items-center justify-center h-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="relative">
            <div className="h-12 w-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
              <Bot className="h-6 w-6 text-violet-400" />
            </div>
            <motion.div
              className="absolute inset-0 rounded-xl border-2 border-violet-500/50"
              animate={{ scale: [1, 1.3, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <motion.div
              className="h-1.5 w-1.5 rounded-full bg-violet-500"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
            />
            <motion.div
              className="h-1.5 w-1.5 rounded-full bg-violet-500"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0.15 }}
            />
            <motion.div
              className="h-1.5 w-1.5 rounded-full bg-violet-500"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0.3 }}
            />
            <span className="ml-2 text-sm">Loading conversation...</span>
          </div>
        </motion.div>
      </div>
    );
  }

  // ─── Render ───
  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Content Area - scrollable */}
      <div className={`relative flex-1 min-h-0 overflow-y-auto ${showEmpty ? "flex flex-col justify-center" : ""}`}>
        <div className={`mx-auto w-full ${panelMode ? "px-3 pt-16 pb-4" : "max-w-5xl px-4 md:px-6 pt-28 pb-4"}`}>
          <AnimatePresence mode="wait">
            {showEmpty ? (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <ChatEmptyState
                  mode={mode}
                  onSuggestionClick={handleSuggestionClick}
                />
              </motion.div>
            ) : (
              <motion.div
                key="timeline"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <ChatTimeline
                  messages={messages}
                  previousRoleByIndex={previousRoleByIndex}
                  multiAgentStateByMessage={multiAgentStateByMessage}
                  streaming={isLoading}
                  compact={panelMode}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Composer */}
      <ChatComposer
        value={input}
        onChange={setInput}
        onSubmit={sendMessage}
        onStop={stopStreaming}
        isLoading={isLoading}
        mode={mode}
        onModeChange={setMode}
        compact={panelMode}
        isDeepResearch={isDeepResearch}
        onDepthToggle={onDepthToggle}
      />
    </div>
  );
};

export type { AssistantChatProps };

export default AssistantChat;

